/**
 * Data layer for `campaign-grouping`. Thin typed fetch wrappers over
 * `/api/v1/campaign-grouping/*`. Raw `fetch()` is confined to this file per
 * the dash contract (rule D6).
 *
 * Backend endpoints (see apps/api/src/routes/campaign-grouping.ts):
 *   GET    /groups
 *   POST   /groups
 *   GET    /groups/:id
 *   PATCH  /groups/:id
 *   DELETE /groups/:id
 *   POST   /groups/:id/members            { campaignId }
 *   DELETE /groups/:id/members/:cid
 *   GET    /groups/:id/campaigns
 */

export interface GroupView {
	id: string;
	name: string;
	color: string;
	created_at: number;
	member_count: number;
}

export interface ListGroupsResponse {
	count: number;
	groups: GroupView[];
}

export interface GroupResponse {
	group: GroupView;
}

export interface GroupCampaignsResponse {
	group_id: string;
	campaign_ids: string[];
	count: number;
}

export interface CreateGroupBody {
	name: string;
	color?: string;
}

export interface PatchGroupBody {
	name?: string;
	color?: string;
}

// Palette restricted to emerald/sky/amber/zinc per dash design. No orange/purple.
export const GROUP_COLOR_PALETTE = [
	{ label: "Emerald", value: "#10b981" },
	{ label: "Sky", value: "#0ea5e9" },
	{ label: "Amber", value: "#f59e0b" },
	{ label: "Zinc", value: "#71717a" },
] as const;

export const DEFAULT_GROUP_COLOR = GROUP_COLOR_PALETTE[0].value;

const BASE = "/api/v1/campaign-grouping";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function listGroups(): Promise<ListGroupsResponse> {
	const res = await fetch(`${BASE}/groups`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListGroupsResponse>(res);
}

export async function createGroup(body: CreateGroupBody): Promise<GroupResponse> {
	const res = await fetch(`${BASE}/groups`, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<GroupResponse>(res);
}

export async function renameGroup(
	id: string,
	body: PatchGroupBody,
): Promise<GroupResponse> {
	const res = await fetch(`${BASE}/groups/${encodeURIComponent(id)}`, {
		method: "PATCH",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<GroupResponse>(res);
}

export async function deleteGroup(id: string): Promise<{ deleted: boolean; id: string }> {
	const res = await fetch(`${BASE}/groups/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ deleted: boolean; id: string }>(res);
}

export async function listCampaignsInGroup(
	groupId: string,
): Promise<GroupCampaignsResponse> {
	const res = await fetch(
		`${BASE}/groups/${encodeURIComponent(groupId)}/campaigns`,
		{
			method: "GET",
			credentials: "include",
			headers: { accept: "application/json" },
		},
	);
	return parseOrThrow<GroupCampaignsResponse>(res);
}

export interface AssignCampaignResponse {
	membership: {
		group_id: string;
		campaign_id: string;
		created_at: number;
	};
	already: boolean;
}

export async function assignCampaign(
	groupId: string,
	campaignId: string,
): Promise<AssignCampaignResponse> {
	const res = await fetch(
		`${BASE}/groups/${encodeURIComponent(groupId)}/members`,
		{
			method: "POST",
			credentials: "include",
			headers: {
				accept: "application/json",
				"content-type": "application/json",
			},
			body: JSON.stringify({ campaignId }),
		},
	);
	return parseOrThrow<AssignCampaignResponse>(res);
}

export async function assignCampaigns(
	groupId: string,
	campaignIds: string[],
): Promise<AssignCampaignResponse[]> {
	const results: AssignCampaignResponse[] = [];
	for (const cid of campaignIds) {
		const r = await assignCampaign(groupId, cid);
		results.push(r);
	}
	return results;
}

export async function removeCampaign(
	groupId: string,
	campaignId: string,
): Promise<{ deleted: boolean; group_id: string; campaign_id: string }> {
	const res = await fetch(
		`${BASE}/groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(
			campaignId,
		)}`,
		{
			method: "DELETE",
			credentials: "include",
			headers: { accept: "application/json" },
		},
	);
	return parseOrThrow<{ deleted: boolean; group_id: string; campaign_id: string }>(
		res,
	);
}

export async function removeCampaigns(
	groupId: string,
	campaignIds: string[],
): Promise<void> {
	for (const cid of campaignIds) {
		await removeCampaign(groupId, cid);
	}
}

export const campaignGroupingQueryKeys = {
	all: ["campaign-grouping"] as const,
	list: () => [...campaignGroupingQueryKeys.all] as const,
	detail: (id: string) => [...campaignGroupingQueryKeys.all, id] as const,
	campaigns: (id: string) =>
		[...campaignGroupingQueryKeys.all, id, "campaigns"] as const,
};
